// src/pages/LogsPage.js
import React, { useState, useEffect } from 'react';
import { getLogs } from '../api';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { Form, Button, Spinner } from 'react-bootstrap';
import Pagination from '../components/Global/Pagination';
import SortableHeader from '../components/ui/SortableHeader';
import { useSorting } from '../hooks/useSorting';
import { es } from 'date-fns/locale';
import useFilterPersistence from '../hooks/useFilterPersistence';

const LogsPage = () => {
  const [textFilters, setTextFilters] = useFilterPersistence('filters_logs', {
    logLevel: '',
    messageContains: '',
    exceptionContains: '',
    userId: '',
    sourceContains: '',
    applicationContains: '',
    httpMethod: '',
    pathContains: '',
    queryStringContains: '',
    controllerContains: '',
    actionContains: '',
    requestBodyContains: '',
    statusCode: '',
    durationMsFrom: '',
    durationMsTo: '',
    ipAddressContains: ''
  });
  // Las fechas no se persisten (objetos Date no son serializables de forma fiable)
  const [filters, setFilters] = useState({
    ...textFilters,
    timestampFrom: new Date(Date.now() - 2 * 60 * 60 * 1000),
    timestampTo: new Date(),
  });
  const [pagination, setPagination] = useFilterPersistence('pagination_logs', { page: 1, pageSize: undefined });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = pagination.page;
  const pageSize = pagination.pageSize;
  const [includeSensitive, setIncludeSensitive] = useState(false);
  const [sensitivePassword, setSensitivePassword] = useState('');
  
  // ordenamiento
  const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting('timestamp', true);

  const toUtcString = date => date.toISOString().slice(0, 19);
  
  const fetchData = async () => {
    setLoading(true);
    try {
        const query = {
            ...filters,
            timestampFrom: toUtcString(filters.timestampFrom),
            timestampTo:   toUtcString(filters.timestampTo),
            includeSensitive,
            password: includeSensitive ? sensitivePassword : undefined,
            userId:        filters.userId   ? Number(filters.userId) : undefined,
            statusCode:    filters.statusCode ? Number(filters.statusCode) : undefined,
            durationMsFrom: filters.durationMsFrom ? Number(filters.durationMsFrom) : undefined,
            durationMsTo:   filters.durationMsTo   ? Number(filters.durationMsTo)   : undefined,
            orderBy,
            orderDesc
            };

        const res = await getLogs(query, currentPage, pageSize);
        setLogs(res.items || []);
        setTotalCount(res.totalCount || 0);
        setSearched(true);
    } catch {
        setLogs([]);
        setTotalCount(0);
        setSearched(true);
    } finally {
        setLoading(false);
    }
  };

  // Sincronizar campos de texto con textFilters al cambiar
  const handleSetFilters = (updater) => {
    setFilters(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const { timestampFrom, timestampTo, ...rest } = next;
      setTextFilters(rest);
      return next;
    });
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchData();
  };

  useEffect(() => {
    if (searched) {
      fetchData();
    }
  }, [orderBy, orderDesc, currentPage, pageSize]);

  return (
    <div className="container-fluid my-4">
      <h2 className="mb-4">Registros de Log</h2>

      {/* filtros */}
      <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
        {[
          { label: 'Mensaje contiene',name: 'messageContains' },
          { label: 'Excepción',       name: 'exceptionContains' },
          { label: 'User ID',         name: 'userId',             type: 'number' },
          { label: 'Fuente',          name: 'sourceContains' },
          { label: 'App',             name: 'applicationContains' },
          { label: 'HTTP',            name: 'httpMethod' },
          { label: 'Ruta contiene',   name: 'pathContains' },
          { label: 'Query contiene',  name: 'queryStringContains' },
          { label: 'Controlador',     name: 'controllerContains' },
          { label: 'Acción',          name: 'actionContains' },
          { label: 'Body contiene',   name: 'requestBodyContains' },
          { label: 'Código',          name: 'statusCode',         type: 'number' },
          { label: 'Duración ≥',      name: 'durationMsFrom',    type: 'number' },
          { label: 'Duración ≤',      name: 'durationMsTo',      type: 'number' },
          { label: 'IP',              name: 'ipAddressContains' }
        ].map(f => (
          <Form.Group key={f.name}>
            <Form.Label>{f.label}</Form.Label>
            <Form.Control
              type={f.type || 'text'}
              value={filters[f.name]}
              onChange={e => handleSetFilters(fs => ({ ...fs, [f.name]: e.target.value }))}
            />
          </Form.Group>
        ))}

        <Form.Group>
            <Form.Label>Desde</Form.Label>
            <DatePicker
                selected={filters.timestampFrom}
                onChange={d => handleSetFilters(f => ({ ...f, timestampFrom: d }))}
                showTimeSelect
                timeFormat="HH:mm:ss"
                timeIntervals={1}
                timeCaption="Hora"
                dateFormat="dd/MM/yyyy HH:mm:ss"
                className="form-control"
                locale={es}
            />
            </Form.Group>

            <Form.Group>
            <Form.Label>Hasta</Form.Label>
            <DatePicker
                selected={filters.timestampTo}
                onChange={d => handleSetFilters(f => ({ ...f, timestampTo: d }))}
                showTimeSelect
                timeFormat="HH:mm:ss"
                timeIntervals={1}
                timeCaption="Hora"
                dateFormat="dd/MM/yyyy HH:mm:ss"
                className="form-control"
                locale={es}
            />
        </Form.Group>
      </div>

      <Form.Group className="d-flex align-items-center">
        <Form.Check
            type="checkbox"
            id="include-sensitive"
            label="Incluir sensibles"
            checked={includeSensitive}
            onChange={e => setIncludeSensitive(e.target.checked)}
            className="me-3"
        />
        {includeSensitive && (
            <Form.Control
            type="password"
            placeholder="Contraseña"
            value={sensitivePassword}
            onChange={e => setSensitivePassword(e.target.value)}
            style={{ maxWidth: '200px' }}
            />
        )}
      </Form.Group>

      <Button onClick={handleSearch} disabled={loading}>
        {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
      </Button>

      {searched && !loading && logs.length === 0 && (
        <div className="text-center text-muted my-4">No hay registros</div>
      )}

      {searched && (
        <>
          <div className="table-responsive w-100 mt-4">
            <table className="table table-striped w-100 text-wrap">
              <thead>
                <tr>
                  <SortableHeader column="message" onSort={handleSort} getSortIcon={getSortIcon}>Mensaje</SortableHeader>
                  <SortableHeader column="exception" onSort={handleSort} getSortIcon={getSortIcon}>Excepción</SortableHeader>
                  <SortableHeader column="userId" onSort={handleSort} getSortIcon={getSortIcon}>UserID</SortableHeader>
                  <SortableHeader column="source" onSort={handleSort} getSortIcon={getSortIcon}>Fuente</SortableHeader>
                  <SortableHeader column="application" onSort={handleSort} getSortIcon={getSortIcon}>App</SortableHeader>
                  <SortableHeader column="httpMethod" onSort={handleSort} getSortIcon={getSortIcon}>HTTP</SortableHeader>
                  <SortableHeader column="path" onSort={handleSort} getSortIcon={getSortIcon}>Ruta</SortableHeader>
                  <SortableHeader column="queryString" onSort={handleSort} getSortIcon={getSortIcon}>Query</SortableHeader>
                  <SortableHeader column="controller" onSort={handleSort} getSortIcon={getSortIcon}>Controlador</SortableHeader>
                  <SortableHeader column="action" onSort={handleSort} getSortIcon={getSortIcon}>Acción</SortableHeader>
                  <SortableHeader column="requestBody" onSort={handleSort} getSortIcon={getSortIcon}>Body</SortableHeader>
                  <SortableHeader column="statusCode" onSort={handleSort} getSortIcon={getSortIcon}>Código</SortableHeader>
                  <SortableHeader column="durationMs" onSort={handleSort} getSortIcon={getSortIcon}>Duración(ms)</SortableHeader>
                  <SortableHeader column="ipAddress" onSort={handleSort} getSortIcon={getSortIcon}>IP</SortableHeader>
                  <SortableHeader column="timestamp" onSort={handleSort} getSortIcon={getSortIcon}>Timestamp</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td>{log.message}</td>
                    <td>{log.exception}</td>
                    <td>{log.userId ?? '—'}</td>
                    <td>{log.source}</td>
                    <td>{log.application}</td>
                    <td>{log.httpMethod}</td>
                    <td>{log.path}</td>
                    <td>{log.queryString}</td>
                    <td>{log.controller}</td>
                    <td>{log.action}</td>
                    <td>{log.requestBody}</td>
                    <td>{log.statusCode}</td>
                    <td>{log.durationMs}</td>
                    <td>{log.ipAddress}</td>
                    <td>{new Date(log.timestamp).toLocaleString('es-ES')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-center mt-4">
            <Pagination
              totalCount={totalCount}
              currentPage={currentPage}
              itemsPerPage={pageSize}
              onPageChange={(page) => setPagination({ ...pagination, page })}
              onItemsPerPageChange={size => setPagination({ page: 1, pageSize: size })}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default LogsPage;
